import { useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { WidgetIconButton } from "../common/WidgetIconButton";
import { STATUS_COLORS } from "../../constants";
import { BoxArrowUpRight } from "react-bootstrap-icons";
import { BaseWidget } from "../common";
import "../../styles/StatusWidget.css";

const STATUS_ORDER = [
  "Accepted",
  "Pending",
  "Delivered",
  "Cancelled",
  "Abandoned",
];

export const StatusWidget = ({
  summary = [],
  isLoading,
  error,
  onNavigate,
}) => {
  const { statusData, total } = useMemo(() => {
    const countsMap = {};
    STATUS_ORDER.forEach((s) => (countsMap[s.toLowerCase()] = 0));
    let runningTotal = 0;

    if (Array.isArray(summary)) {
      summary.forEach((item) => {
        const rawStatus = (item.status || item.Status || "").toString();
        const count = item.count ?? item.Count ?? 0;
        const key = rawStatus.toLowerCase();
        if (countsMap[key] !== undefined) countsMap[key] += count;
        runningTotal += count;
      });
    }

    const formattedData = STATUS_ORDER.map((name) => {
      const value = countsMap[name.toLowerCase()] || 0;
      return {
        name,
        value,
        percent: runningTotal > 0 ? (value / runningTotal) * 100 : 0,
        color: STATUS_COLORS[name] || "#9ca3af",
      };
    });

    return { statusData: formattedData, total: runningTotal };
  }, [summary]);

  const handleStatusClick = (statusName) => {
    const params = new URLSearchParams();
    if (statusName) {
      params.append("status", statusName);
    }
    onNavigate(`/contracts?${params.toString()}`);
  };

  const donutData =
    total > 0
      ? statusData.filter((s) => s.value > 0)
      : [{ name: "No Data", value: 1, color: "#e5e7eb" }];

  return (
    <BaseWidget
      title="Contracts by Status"
      isLoading={isLoading}
      error={error}
      headerRight={
        <>
          <WidgetIconButton
            icon={<BoxArrowUpRight />}
            title="View all"
            onClick={() => handleStatusClick()}
          />
        </>
      }
    >
      <Row className="g-3 align-items-center">
        <Col xs="auto">
          <div
            className="cp-status-widget__donut"
            style={{ width: 140, height: 180, position: "relative" }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  cx="50%"
                  cy="50%"
                  innerRadius="65%"
                  outerRadius="95%"
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                  isAnimationActive={false}
                >
                  {donutData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div
              className="cp-status-widget__donut-center"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
              }}
            >
              <div className="fs-4 fw-bold">{total.toLocaleString()}</div>
            </div>
          </div>
        </Col>

        <Col className="cp-status-widget__legend-col">
          <div className="d-flex flex-column gap-1">
            {statusData.map((s) => (
              <button
                key={s.name}
                type="button"
                className="btn btn-link text-decoration-none text-dark p-0 border-0"
                disabled={s.value === 0}
                onClick={() => handleStatusClick(s.name)}
              >
                <Row className="g-2 align-items-center w-100">
                  <Col xs="auto">
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: s.color,
                      }}
                    />
                  </Col>
                  <Col className="text-start text-nowrap">{s.name}</Col>
                  <Col xs="auto" className="text-end ms-auto">
                    <span className="fw-bold">{s.value.toLocaleString()}</span>
                    <span className="text-muted small ms-2">
                      {s.percent.toFixed(0)}%
                    </span>
                  </Col>
                </Row>
              </button>
            ))}
          </div>
        </Col>
      </Row>
    </BaseWidget>
  );
};
